// MemberEvents.jsx — Mes événements (À venir + Historique avec présence)
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { mediaUrl } from '@/lib/media';
import { Calendar, MapPin, Ticket, Loader2, Check, X, History, Sparkles, Award } from 'lucide-react';
import { toast } from 'sonner';

export default function MemberEvents() {
  const [tab, setTab] = useState('a-venir');
  const [evts, setEvts] = useState([]);
  const [historique, setHistorique] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reserving, setReserving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [e, h] = await Promise.all([
        api.get('/evenements'),
        api.get('/presences/me/historique').catch(() => ({ data: null })),
      ]);
      setEvts(e.data); setHistorique(h.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reserver = async (e) => {
    setReserving(e.id);
    try {
      const tarif = (Array.isArray(e.tarifs) ? e.tarifs[0] : null) || { label: 'Standard', prix_membre: 0 };
      await api.post('/billets', { evenement_id: e.id, categorie: tarif.label, prix_paye: tarif.prix_membre || 0 });
      toast.success(`Billet réservé pour "${e.nom}"`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setReserving(null); }
  };

  if (loading) return <div className="p-8 grid place-items-center min-h-[60vh]"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;

  const aVenir = evts.filter((e) => new Date(e.date_debut) >= new Date());

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="member-events">
      <div className="mb-8 slide-up">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--theme-primary)' }}>Événements</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02]">
          Vos <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>rendez-vous.</span>
        </h1>
      </div>

      {/* Stats historique */}
      {historique && historique.total_evenements_inscrits > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 stagger" data-testid="presence-stats">
          <Stat icon={Check} label="Présent" value={historique.total_presents} sub={`/ ${historique.total_evenements_inscrits} inscrits`} color="#10B981" testid="stat-presents" />
          <Stat icon={Award} label="Taux présence" value={historique.taux_presence !== null ? `${historique.taux_presence}%` : '—'} sub="depuis adhésion" testid="stat-taux" />
          <Stat icon={Sparkles} label="Statut" value={historique.adherent ? 'Adhérent' : 'Non adhérent'} sub={historique.formule || ''} testid="stat-statut" />
          <Stat icon={History} label="1ère visite" value={historique.first_visit_at ? new Date(historique.first_visit_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'} sub={historique.first_visit_at ? new Date(historique.first_visit_at).getFullYear() : '—'} testid="stat-first-visit" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#E2E8F0] mb-6">
        <TabBtn active={tab === 'a-venir'} onClick={() => setTab('a-venir')} count={aVenir.length} testid="tab-a-venir">À venir</TabBtn>
        <TabBtn active={tab === 'historique'} onClick={() => setTab('historique')} count={historique?.historique?.length || 0} testid="tab-historique">Historique</TabBtn>
      </div>

      {tab === 'a-venir' && (
        aVenir.length === 0 ? <Empty title="Aucun événement programmé" desc="Revenez bientôt pour découvrir les prochaines rencontres." />
        : <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger" data-testid="member-events-grid">
            {aVenir.map((e) => <EventCard key={e.id} evt={e} onReserve={() => reserver(e)} reserving={reserving === e.id} />)}
          </div>
      )}

      {tab === 'historique' && (
        !historique || historique.historique.length === 0 ? <Empty title="Pas encore d'historique" desc="Votre participation à des événements sera affichée ici." />
        : <div className="space-y-3" data-testid="historique-list">
            {historique.historique.filter((h) => new Date(h.date_debut) < new Date()).map((h) => <HistoriqueRow key={h.evenement_id} h={h} />)}
          </div>
      )}
    </div>
  );
}

function Stat({ icon: Ic, value, label, sub, color, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] truncate">{label}</div>
        <Ic className="w-4 h-4 shrink-0 ml-2" style={{ color: color || 'var(--theme-primary)' }} />
      </div>
      <div className="font-display text-2xl sm:text-3xl font-black tracking-tight text-[#0F172A]">{value}</div>
      <div className="text-[10px] text-[#94A3B8] mt-1 truncate">{sub}</div>
    </div>
  );
}

function TabBtn({ active, onClick, count, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`relative px-5 py-3 text-sm font-semibold transition-colors ${active ? 'text-[#0F172A]' : 'text-[#94A3B8] hover:text-[#475569]'}`}>
      {children}
      {count > 0 && <span className={`ml-2 text-[10px] font-black px-1.5 py-0.5 rounded-full ${active ? 'bg-[var(--theme-primary)] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}>{count}</span>}
      {active && <span className="absolute -bottom-px inset-x-0 h-0.5 bg-[var(--theme-primary)]" />}
    </button>
  );
}

function EventCard({ evt, onReserve, reserving }) {
  const tarif = Array.isArray(evt.tarifs) && evt.tarifs[0];
  return (
    <div className="card card-hover">
      {evt.image_url && (
        <img src={mediaUrl(evt.image_url)} alt={evt.nom} className="w-full h-36 object-cover rounded-xl mb-3" data-testid={`member-evt-image-${evt.id}`} />
      )}
      <div className="flex items-start justify-between mb-3">
        <span className="badge badge-primary capitalize">{evt.type}</span>
        {tarif && <div className="text-right"><div className="font-display text-xl font-black text-[#0F172A]">{tarif.prix_membre || 0}€</div><div className="text-[10px] text-[#475569] uppercase tracking-wider">membre</div></div>}
      </div>
      <h3 className="font-display text-xl font-bold text-[#0F172A] mb-2">{evt.nom}</h3>
      <p className="text-sm text-[#475569] mb-4 line-clamp-2 min-h-[2.5rem]">{evt.description || ''}</p>
      <div className="space-y-1.5 text-xs text-[#475569] mb-4">
        <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-[#94A3B8]" /> {new Date(evt.date_debut).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}</div>
        {evt.lieu && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#94A3B8]" /> {evt.lieu}</div>}
      </div>
      {evt.billetterie ? (
        <button onClick={onReserve} disabled={reserving} className="btn-primary w-full flex items-center justify-center gap-2" data-testid={`member-reserve-${evt.id}`}>
          {reserving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ticket className="w-4 h-4" /> Réserver mon billet</>}
        </button>
      ) : (
        <div className="text-center text-xs text-[#475569] py-2 flex items-center justify-center gap-1.5"><Check className="w-3 h-3" /> Accès libre aux membres</div>
      )}
    </div>
  );
}

function HistoriqueRow({ h }) {
  const date = new Date(h.date_debut);
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 flex items-center gap-4" data-testid={`historique-${h.evenement_id}`}>
      <div className={`w-12 h-12 rounded-2xl grid place-items-center shrink-0 ${h.present ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#FEE2E2] text-[#DC2626]'}`}>
        {h.present ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <h4 className="font-semibold text-[#0F172A] truncate">{h.evenement_nom}</h4>
          {h.present ? <span className="badge badge-success">Présent</span> : <span className="badge badge-danger">Absent</span>}
        </div>
        <div className="text-xs text-[#475569] flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          {h.lieu && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {h.lieu}</span>}
          {h.method && <span className="text-[#94A3B8]">· {h.method === 'qr' ? 'QR' : 'manuel'}</span>}
        </div>
      </div>
      {h.prix_paye > 0 && <div className="text-right shrink-0"><div className="font-mono text-sm font-bold text-[#0F172A]">{h.prix_paye}€</div></div>}
    </div>
  );
}

function Empty({ title, desc }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-12 text-center">
      <Calendar className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
      <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">{title}</h3>
      <p className="text-sm text-[#475569]">{desc}</p>
    </div>
  );
}
